/**
 * HelpAssistantBubble
 * --------------------------------------------------------------------------
 * Floating chat bubble bottom-right — GPT-5.2-powered AeroGuard help
 * assistant grounded in the article corpus (never speculates on features
 * that aren't documented, and refuses off-topic queries).
 *
 * UX for lower-knowledge workers:
 *   - "Plain-English mode" toggle (calls backend with reading_level=simple)
 *   - Role-aware quick-question chips so users don't need to know what to ask
 *   - "Read aloud" button on every AI answer (browser SpeechSynthesis, offline)
 *   - Larger, higher-contrast layout, no aviation jargon in the shell UI
 */
import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, X, Send, Loader2, Volume2, VolumeX, BookOpen, Wand2, Mic, MicOff, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role-tailored quick-question chips. Kept plain and task-oriented — a new
// user who doesn't know acronyms can still tap something meaningful.
const QUICK_QUESTIONS_BY_ROLE = {
  admin: [
    'How do I add a new user?',
    'Where do I set up regulatory authorities?',
    'How do I check my Universal Key balance?',
    'How does the AI Help Assistant work?',
  ],
  safety_manager: [
    'What is on my daily checklist?',
    'How do I replay a stuck submission (DLQ)?',
    'How does the AI Field Suggestions strip work?',
    'What is a CAPA effectiveness check?',
  ],
  safety_officer: [
    'How do I file an incident report?',
    'How do I file a report by voice?',
    'What does "Polish my description" do?',
    'How do I attach a photo to a report?',
  ],
  auditor: [
    'How do I attach evidence to an audit item?',
    'How do I generate a CAPA from an audit gap?',
    'Can I control the app by voice?',
    'What does GACAR Part 5 require?',
  ],
  investigator: [
    'How do I use the AI Root Cause suggestion?',
    'How do I record a witness interview?',
    'How does multi-turn AI memory work?',
    'How do I close an investigation?',
  ],
  inspector: [
    'What can I see as an Inspector?',
    'How do I use Inspector Mode?',
    'Can I listen to a help article?',
    'How do I switch to Arabic?',
  ],
  default: [
    'How do I file an incident?',
    'How do I file a report by voice?',
    'What does CAPA mean?',
    'Can I control the app by voice?',
  ],
};

export default function HelpAssistantBubble({ token, role, lang = 'en' }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  // Per-article thumbs vote cache — { [articleId]: 'up' | 'down' | null }.
  // Rendered next to every cited article so the CEO can prioritise which
  // topics to expand based on real user signal.
  const [articleVotes, setArticleVotes] = useState({});
  // Plain-English mode ON by default — safer for the majority of users;
  // power users can turn it off if they want the full aviation register.
  const [simpleMode, setSimpleMode] = useState(true);
  // Per-panel language override. Defaults to the app-wide language toggle
  // but the user can flip EN⇄AR without switching the whole UI.
  const [answerLang, setAnswerLang] = useState(lang);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);

  // Follow the app-wide language if the user changes it externally.
  useEffect(() => { setAnswerLang(lang); }, [lang]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Stop any in-flight speech when the panel closes / component unmounts
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (!open && typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeakingIdx(null);
    }
  }, [open]);

  const quickQuestions = QUICK_QUESTIONS_BY_ROLE[role] || QUICK_QUESTIONS_BY_ROLE.default;

  const speak = (text, idx, msgLang) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      toast.error('Your browser does not support read-aloud.');
      return;
    }
    // Toggle off if the same message is already being read
    if (speakingIdx === idx) {
      window.speechSynthesis.cancel();
      setSpeakingIdx(null);
      return;
    }
    window.speechSynthesis.cancel();
    // Strip markdown syntax that would otherwise be read literally
    const clean = text
      .replace(/[*_`#>]/g, '')
      .replace(/→/g, ', then ')
      .replace(/\s+/g, ' ')
      .trim();
    const utter = new SpeechSynthesisUtterance(clean);
    const effectiveLang = msgLang || answerLang || lang;
    utter.lang = effectiveLang === 'ar' ? 'ar-SA' : 'en-US';
    utter.rate = 0.95;
    utter.onend = () => setSpeakingIdx(null);
    utter.onerror = () => setSpeakingIdx(null);
    setSpeakingIdx(idx);
    window.speechSynthesis.speak(utter);
  };

  // ---- Voice-to-text using the browser's Web Speech API --------------
  const toggleListening = () => {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const SR = typeof window !== 'undefined'
      && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) {
      toast.error('Voice input is not supported in this browser. Try Chrome or Edge.');
      return;
    }
    try {
      const rec = new SR();
      rec.lang = answerLang === 'ar' ? 'ar-SA' : 'en-US';
      rec.interimResults = true;
      rec.continuous = false;
      rec.maxAlternatives = 1;
      rec.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((r) => r[0].transcript)
          .join(' ');
        setQuestion(transcript);
      };
      rec.onerror = (e) => {
        setListening(false);
        // 'aborted' fires whenever the user stops manually — not an error
        if (e.error && e.error !== 'aborted' && e.error !== 'no-speech') {
          toast.error(`Voice input error: ${e.error}`);
        }
      };
      rec.onend = () => setListening(false);
      recognitionRef.current = rec;
      setListening(true);
      rec.start();
    } catch (err) {
      setListening(false);
      toast.error(`Voice input failed: ${err.message}`);
    }
  };

  // Cancel any in-flight recognition when panel closes
  useEffect(() => {
    if (!open && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      setListening(false);
    }
  }, [open]);

  // ---- Thumbs up / down feedback ------------------------------------
  // Toggle an article vote — POST 'up'/'down' or DELETE to retract when the
  // user clicks the same thumb twice.
  const voteArticle = async (articleId, rating) => {
    const current = articleVotes[articleId] || null;
    const shouldRetract = current === rating;
    // Optimistic update
    setArticleVotes((prev) => ({ ...prev, [articleId]: shouldRetract ? null : rating }));
    try {
      if (shouldRetract) {
        const r = await fetch(`${API_URL}/api/help/feedback/article/${articleId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        toast.success('Vote retracted');
      } else {
        const r = await fetch(`${API_URL}/api/help/feedback/article/${articleId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ rating }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        toast.success(rating === 'up' ? 'Thanks — noted' : 'Thanks — we\'ll revisit this article');
      }
    } catch (e) {
      // Roll back optimistic update
      setArticleVotes((prev) => ({ ...prev, [articleId]: current }));
      toast.error(`Couldn't save vote: ${e.message}`);
    }
  };

  const sendFeedback = async (msgIdx, rating) => {
    const msg = messages[msgIdx];
    if (!msg || msg.role !== 'assistant' || msg.feedback) return;
    // Find the immediately-preceding user question
    let userQ = '';
    for (let i = msgIdx - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') { userQ = messages[i].text; break; }
    }
    // Optimistic UI update — don't wait for the server
    setMessages((prev) => prev.map((m, i) => (i === msgIdx ? { ...m, feedback: rating } : m)));
    try {
      const r = await fetch(`${API_URL}/api/help/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          question: userQ || '(unknown)',
          answer_preview: (msg.text || '').slice(0, 1000),
          rating,
          role,
          lang,
          // Send the cited article ids so the admin summary can attribute
          // per-article "AI answer pain" — critical for prioritising rewrites.
          article_ids: (msg.cited || []).map((c) => (typeof c === 'string' ? c : c.id)).filter(Boolean),
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(rating === 'up' ? 'Thanks — glad that helped!' : 'Thanks — we\'ll improve this answer.');
    } catch (e) {
      // Roll back optimistic update if the request failed
      setMessages((prev) => prev.map((m, i) => (i === msgIdx ? { ...m, feedback: undefined } : m)));
      toast.error(`Couldn't save feedback: ${e.message}`);
    }
  };

  const askQuestion = async (q) => {
    if (!q || loading) return;
    setQuestion('');
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    setLoading(true);
    // Send the last 6 messages (= 3 back-and-forth exchanges) so the AI can
    // resolve follow-ups like "and how do I close it?". We deliberately keep
    // this small — a longer window would blow past the 200-word answer style
    // and inflate token cost.
    const history = messages.slice(-6).map((m) => ({
      role: m.role === 'assistant' ? 'assistant' : 'user',
      text: m.text || '',
    }));
    try {
      const r = await fetch(`${API_URL}/api/help/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          question: q,
          role,
          lang: answerLang,
          reading_level: simpleMode ? 'simple' : 'standard',
          history,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: j.answer,
          cited: j.cited_articles || [],
          refused: !!j.refused,
          lang: j.lang || answerLang,
        },
      ]);
    } catch (e) {
      toast.error(`Assistant failed: ${e.message}`);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: `Sorry — I couldn't reach the assistant. ${e.message}`, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const ask = () => askQuestion(question.trim());

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 rounded-full bg-gradient-to-br from-violet-500 to-sky-500 shadow-lg shadow-violet-500/30 hover:shadow-xl transition-all p-4 group"
          data-testid="help-assistant-bubble"
          aria-label="Open AI Help Assistant"
        >
          <Sparkles className="w-5 h-5 text-white" />
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Need help? Ask me
          </span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[min(440px,calc(100vw-2rem))] h-[min(620px,calc(100vh-3rem))] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/40 flex flex-col overflow-hidden"
          data-testid="help-assistant-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-violet-500/20 to-sky-500/10 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-300" />
              <span className="font-bold text-white text-sm">AeroGuard Help</span>
              <Badge className="bg-slate-900 border border-slate-700 text-[10px] text-slate-400">
                AeroGuard-only
              </Badge>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-white p-1 rounded"
              aria-label="Close assistant"
              data-testid="help-assistant-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Mode toggle bar */}
          <div className="flex items-center justify-between px-3 py-2 bg-slate-900/70 border-b border-slate-800 gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSimpleMode((v) => !v)}
                data-testid="help-simple-mode-toggle"
                className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full transition-colors border ${
                  simpleMode
                    ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40'
                    : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-slate-200'
                }`}
                aria-pressed={simpleMode}
                title={simpleMode ? 'Plain-English mode is ON' : 'Plain-English mode is OFF'}
              >
                <Wand2 className="w-3 h-3" />
                {simpleMode ? 'Plain English: ON' : 'Plain English: OFF'}
              </button>
              {/* EN | AR language selector — override the app-wide toggle per chat. */}
              <div
                role="group"
                aria-label="Answer language"
                className="flex items-center rounded-full border border-slate-700 overflow-hidden text-[11px]"
                data-testid="help-lang-toggle"
              >
                <button
                  type="button"
                  onClick={() => setAnswerLang('en')}
                  className={`px-2.5 py-1 transition-colors ${
                    answerLang === 'en'
                      ? 'bg-cyan-500/20 text-cyan-200'
                      : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                  }`}
                  aria-pressed={answerLang === 'en'}
                  data-testid="help-lang-en"
                  title="Answer in English"
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => setAnswerLang('ar')}
                  className={`px-2.5 py-1 transition-colors ${
                    answerLang === 'ar'
                      ? 'bg-cyan-500/20 text-cyan-200'
                      : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                  }`}
                  aria-pressed={answerLang === 'ar'}
                  data-testid="help-lang-ar"
                  title="أجب بالعربية"
                >
                  AR
                </button>
              </div>
            </div>
            <span className="text-[10px] text-slate-500 italic">
              {answerLang === 'ar'
                ? 'أجيب عن أسئلة AeroGuard وسلامة الطيران فقط.'
                : 'I answer only about AeroGuard & aviation safety.'}
            </span>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-3 space-y-3"
            data-testid="help-assistant-messages"
          >
            {messages.length === 0 && (
              <div className="text-center text-slate-500 text-sm py-4 space-y-3">
                <Sparkles className="w-8 h-8 text-violet-400 mx-auto opacity-70" />
                <div className="font-semibold text-slate-200 text-base">
                  Hi! I can help you use AeroGuard.
                </div>
                <div className="text-slate-400 text-xs max-w-[300px] mx-auto leading-relaxed">
                  Tap a question below, or type your own. I only answer about AeroGuard
                  and aviation safety — I won&apos;t make things up.
                </div>
                <div className="mt-3 space-y-2 text-left px-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <BookOpen className="w-3 h-3" /> Common questions for your role
                  </div>
                  {quickQuestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => askQuestion(s)}
                      data-testid={`help-quick-question-${s.slice(0, 20).replace(/\s+/g, '-')}`}
                      className="block w-full text-left text-xs text-sky-200 bg-slate-800/70 hover:bg-slate-800 border border-slate-700 hover:border-sky-500/50 rounded-md px-3 py-2 transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`help-msg-${m.role}-${i}`}
              >
                <div
                  dir={m.lang === 'ar' ? 'rtl' : 'ltr'}
                  className={`max-w-[88%] rounded-lg px-3 py-2 text-[13px] leading-relaxed whitespace-pre-line ${
                    m.role === 'user'
                      ? 'bg-sky-500/20 border border-sky-500/40 text-sky-100'
                      : m.error
                        ? 'bg-rose-500/10 border border-rose-500/40 text-rose-200'
                        : m.refused
                          ? 'bg-amber-500/10 border border-amber-500/40 text-amber-100'
                          : 'bg-slate-800 border border-slate-700 text-slate-100'
                  }`}
                >
                  {m.text}
                  {m.role === 'assistant' && !m.error && (
                    <div className="mt-2 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => speak(m.text, i, m.lang)}
                        data-testid={`help-read-aloud-${i}`}
                        className="inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-sky-300"
                        aria-label={speakingIdx === i ? 'Stop reading' : 'Read aloud'}
                      >
                        {speakingIdx === i ? (
                          <><VolumeX className="w-3 h-3" /> Stop</>
                        ) : (
                          <><Volume2 className="w-3 h-3" /> Read aloud</>
                        )}
                      </button>
                      {!m.refused && (
                        <div className="flex items-center gap-1 ml-auto" data-testid={`help-feedback-${i}`}>
                          <button
                            type="button"
                            onClick={() => sendFeedback(i, 'up')}
                            disabled={!!m.feedback}
                            aria-label="Helpful"
                            data-testid={`help-thumbs-up-${i}`}
                            className={`p-1 rounded transition-colors ${
                              m.feedback === 'up'
                                ? 'text-emerald-400 bg-emerald-500/15'
                                : 'text-slate-500 hover:text-emerald-400'
                            } ${m.feedback ? 'cursor-default' : 'cursor-pointer'}`}
                          >
                            <ThumbsUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => sendFeedback(i, 'down')}
                            disabled={!!m.feedback}
                            aria-label="Not helpful"
                            data-testid={`help-thumbs-down-${i}`}
                            className={`p-1 rounded transition-colors ${
                              m.feedback === 'down'
                                ? 'text-rose-400 bg-rose-500/15'
                                : 'text-slate-500 hover:text-rose-400'
                            } ${m.feedback ? 'cursor-default' : 'cursor-pointer'}`}
                          >
                            <ThumbsDown className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {m.cited && m.cited.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-700 space-y-1" data-testid={`help-cited-${i}`}>
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">From the user guide</div>
                      {m.cited.map((c) => {
                        const vote = articleVotes[c.id];
                        return (
                          <div key={c.id} className="flex items-center gap-2 text-[10px] text-violet-300">
                            <span className="flex-1 truncate">↳ {c.title}</span>
                            <button
                              type="button"
                              onClick={() => voteArticle(c.id, 'up')}
                              className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded border transition ${
                                vote === 'up'
                                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                                  : 'bg-transparent border-slate-700 text-slate-500 hover:text-emerald-300 hover:border-emerald-700'
                              }`}
                              aria-label={`Thumbs up ${c.title}`}
                              data-testid={`help-article-up-${c.id}-${i}`}
                            >
                              <ThumbsUp className="w-2.5 h-2.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => voteArticle(c.id, 'down')}
                              className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded border transition ${
                                vote === 'down'
                                  ? 'bg-rose-500/20 border-rose-500/50 text-rose-300'
                                  : 'bg-transparent border-slate-700 text-slate-500 hover:text-rose-300 hover:border-rose-700'
                              }`}
                              aria-label={`Thumbs down ${c.title}`}
                              data-testid={`help-article-down-${c.id}-${i}`}
                            >
                              <ThumbsDown className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin text-violet-400" />
                  <span className="text-xs text-slate-400">Looking it up in the user guide…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-700 flex items-center gap-2 bg-slate-900/60">
            <button
              type="button"
              onClick={toggleListening}
              disabled={loading}
              data-testid="help-voice-btn"
              aria-label={listening ? 'Stop voice input' : 'Speak your question'}
              className={`h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-md border transition-colors ${
                listening
                  ? 'bg-rose-500/20 border-rose-500/50 text-rose-300 animate-pulse'
                  : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-sky-300 hover:border-sky-500/40'
              }`}
              title={listening ? 'Stop listening' : 'Speak your question'}
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
              placeholder={
                listening
                  ? 'Listening…'
                  : simpleMode
                    ? 'Ask in plain English…'
                    : 'Type your question…'
              }
              disabled={loading}
              className="bg-slate-900 border-slate-700 h-9 text-sm"
              data-testid="help-assistant-input"
            />
            <Button
              type="button"
              onClick={ask}
              disabled={loading || !question.trim()}
              size="sm"
              className="h-9 px-3 bg-violet-500 hover:bg-violet-600 text-white"
              data-testid="help-assistant-send"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
